import axios from "axios";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import { AccountType, ClientId, Cusip, Custodian, CustodianAccountId, DateSchema, PortfolioId, Ticker, } from "../../Types";
import ValidationError from "../../ZodError";
const AccountInfo = z.object({
    custodianAccountId: CustodianAccountId,
    custodianName: Custodian,
    displayName: z.string(),
    accountType: AccountType,
});
const AccountItem = z.object({
    account: AccountInfo,
    price: z.number(),
    totalMarketValue: z.number(),
    totalQuantity: z.number(),
    weight: z.number(),
});
const Subtotal = z.object({
    price: z.number(),
    targetWeight: z.number(),
    targetWeightDifference: z.number(),
    totalMarketValue: z.number(),
    totalQuantity: z.number(),
    weight: z.number(),
});
const AggregatedItem = z.object({
    accountItems: AccountItem.array(),
    subtotal: Subtotal,
    cusip: Cusip,
    ticker: Ticker,
    description: z.string().nullable(),
});
const Total = z.object({
    targetWeight: z.number(),
    targetWeightDifference: z.number(),
    totalMarketValue: z.number(),
    weight: z.number(),
});
const PostTradeDetails = z.object({
    aggregatedItems: AggregatedItem.array(),
    total: Total,
});
const CapitalConsequences = z.object({
    estimatedLongTermGainsDollars: z.number(),
    estimatedLongTermLossesDollars: z.number(),
    estimatedLongTermTaxesDollars: z.number(),
    estimatedShortTermGainsDollars: z.number(),
    estimatedShortTermLossesDollars: z.number(),
    estimatedShortTermTaxesDollars: z.number(),
});
const Holding = z.object({
    cusip: Cusip,
    ticker: Ticker,
    description: z.string(),
    marketValue: z.number(),
    quantity: z.number(),
    weight: z.number(),
});
const CurrentHoldingsDetails = z.object({
    holdings: Holding.array(),
    totalMarketValue: z.number(),
});
const AccountInTradingGroup = z.object({
    info: AccountInfo,
    metadata: z.object({
        isAccountBalanceNonPositive: z.boolean(),
        isAccountCashNonPositive: z.boolean(),
        isAccountHoldingMissingTicker: z.boolean(),
    }),
});
const TradingGroupInfo = z.object({
    accounts: AccountInTradingGroup.array(),
    isActive: z.boolean(),
    taxLossHarvesting: z.boolean(),
    isNewerVersionAvailable: z.boolean(),
});
const ProposalDetails = z.object({
    clientId: ClientId,
    hypotheticalTradingGroup: TradingGroupInfo,
    targetPortfolio: z.object({
        fartherId: PortfolioId,
        fartherName: z.string(),
    }),
    status: z.object({
        internal: z.string(),
    }),
    modifiedOn: DateSchema,
    createdOn: DateSchema,
});
const Trade = z.object({
    account: AccountInfo,
    cusip: Cusip,
    ticker: Ticker,
    description: z.string().nullable(),
    side: z.enum(["BUY", "SELL"]),
    quantity: z.number(),
    price: z.number().nullable(),
    marketValue: z.number().nullable(),
    isInTargetPortfolio: z.boolean(),
});
const TradeSummary = z.object({
    diffTickers: z.number(),
    sideCount: z.number(),
    quantity: z.number(),
    sum: z.number().nullable(),
});
const ProposedTradeDetails = z.object({
    trades: Trade.array(),
    summary: z.object({
        totalBuys: TradeSummary,
        totalSells: TradeSummary,
    }),
});
const YTDNetGainsRealized = z.object({
    longTermGainsDollars: z.number(),
    shortTermGainsDollars: z.number(),
    totalDollars: z.number(),
});
const Proposal = z.object({
    aggregatedPostTradeDetails: PostTradeDetails,
    capitalConsequences: CapitalConsequences,
    currentHoldingsDetails: CurrentHoldingsDetails,
    isConfirmable: z.boolean(),
    includesTaxAdvantagedAccount: z.boolean(),
    lowBalanceDisclaimerWarning: z.boolean(),
    proposal: ProposalDetails,
    proposedTrades: ProposedTradeDetails,
    ytdNetGainsRealized: YTDNetGainsRealized,
    ytdTaxDollars: z.number(),
});
export const HypotheticalProposalResponse = z.object({
    withBudgetProposals: Proposal.array(),
    withoutBudgetProposals: Proposal.array(),
});
export const url = () => environment() === "PROD"
    ? `https://middleware.prod.farther.com/clientops/v4/proposals-management/hypothetical`
    : `https://middleware.uat.farther.com/clientops/v4/proposals-management/hypothetical`;
export default async function postHypotheticalTaxBudget(hypotheticalProposal, config) {
    const { data } = await axios.post(url(), hypotheticalProposal, {
        headers: {
            Authorization: `Bearer ${config.jwt}`,
            Client: config.clientId,
            ...("advisorId" in config && { Advisor: config.advisorId }),
        },
    });
    const decoded = HypotheticalProposalResponse.safeParse(data);
    if (decoded.success) {
        return decoded.data;
    }
    else {
        const errorMessage = "[postHypotheticalTaxBudget] returned unexpected data.";
        errorLog(postHypotheticalTaxBudget.name, JSON.stringify(decoded.error.issues));
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
}
//# sourceMappingURL=postHypotheticalTaxBudget.js.map